import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './SubmissionDetail.css';

const STATUS_OPTIONS = ['submitted', 'reviewing', 'approved', 'rejected'];

// Product type labels
const TYPE_LABELS = {
  // Jerseys
  'full-button': 'Full Button',
  'two-button': 'Two-Button Henley',
  'v-neck': 'V-Neck',
  'crew-neck': 'Crew Neck',
  // Pants
  'open-bottom': 'Open Bottom',
  'elastic-bottom': 'Elastic Bottom',
  'standard': 'Standard Baseball Pants',
  'knicker': 'Knicker',
  'shorts': 'Shorts',
  // Socks
  'crew': 'Crew Socks',
  'over-calf': 'Over the Calf',
  'over-the-calf': 'Over the Calf',
  'stirrup': 'Stirrup Socks',
  // Caps
  'fitted': 'Fitted Cap',
  'adjustable': 'Adjustable',
  'snapback': 'Snapback',
  'flexfit': 'Flexfit',
  'trucker': 'Trucker',
  'visor': 'Visor',
};

// Size order for sorting (smallest to largest) - includes all possible size codes
const SIZE_ORDER = [
  // Jersey/Pants Youth sizes
  'YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL',
  // Jersey/Pants Adult sizes
  'AXS', 'AS', 'AM', 'AL', 'AXL', 'A2X', 'A3X',
  // Coach sizes
  'S', 'M', 'L', 'XL', '2X', '3X',
  // Socks sizes
  'XS', '6-8', '9-11', '10-13',
  // Generic sizes
  'XXS', '2XL', '3XL',
  // Belts/Caps sizes
  'Youth', 'Adult'
];

// Sock size labels - matches League Uniform Management exactly
const SOCK_SIZE_LABELS = {
  'XS': 'XS (Youth 8-12)',
  'S': 'Small (Youth 12-5/Ladies 4-7)',
  '6-8': 'Medium (Men 6-8/Ladies 7-10)',
  '9-11': 'Large (Men 9-12/Ladies 10-13)',
  '10-13': 'XL (Mens 12-15/Ladies 13+)',
};

// Check if sizes object is nested (jersey/pants with youth/adult/coach)
const isNestedSizes = (sizes) => {
  if (!sizes || typeof sizes !== 'object') return false;
  return 'youth' in sizes || 'adult' in sizes || 'coach' in sizes;
};

// Helper to format flat sizes object into readable string (sorted smallest to largest)
const formatFlatSizes = (sizes, useSockLabels = false) => {
  if (!sizes) return '-';
  const entries = Object.entries(sizes).filter(([_, qty]) => parseInt(qty) > 0);
  if (entries.length === 0) return '-';

  // Sort by size order
  entries.sort((a, b) => {
    const indexA = SIZE_ORDER.indexOf(a[0]);
    const indexB = SIZE_ORDER.indexOf(b[0]);
    // If not in SIZE_ORDER, put at end
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  return entries.map(([size, qty]) => {
    const label = useSockLabels && SOCK_SIZE_LABELS[size] ? SOCK_SIZE_LABELS[size] : size;
    return `${label}: ${qty}`;
  }).join(', ');
};

// Helper to format sock sizes with full labels
const formatSockSizes = (sizes) => formatFlatSizes(sizes, true);

// Helper to format nested sizes (youth/adult/coach) into readable string
const formatNestedSizes = (sizes) => {
  if (!sizes) return '-';
  const parts = [];

  ['youth', 'adult', 'coach'].forEach(category => {
    if (sizes[category]) {
      const catSizes = formatFlatSizes(sizes[category]);
      if (catSizes !== '-') {
        const label = category.charAt(0).toUpperCase() + category.slice(1);
        parts.push(`${label}: ${catSizes}`);
      }
    }
  });

  return parts.length > 0 ? parts.join(' | ') : '-';
};

// Unified formatSizes that handles both flat and nested
const formatSizes = (sizes) => {
  if (!sizes) return '-';
  if (isNestedSizes(sizes)) {
    return formatNestedSizes(sizes);
  }
  return formatFlatSizes(sizes);
};

// Helper to get all teams from divisions (grouped by division)
const getAllTeams = (divisions) => {
  const teams = [];
  if (!divisions) return teams;
  divisions.forEach((division, divIndex) => {
    if (!division.teams) return;
    division.teams.forEach((team, teamIndex) => {
      teams.push({
        key: `${divIndex}-${teamIndex}`,
        teamName: team.name || `Team ${teamIndex + 1}`,
        teamColor: team.color,
        teamSponsor: team.sponsorName || '',
        divisionName: division.name || `Division ${divIndex + 1}`,
        divisionIndex: divIndex,
      });
    });
  });
  return teams;
};

// Helper to get total from nested sizes (youth/adult/coach)
const getNestedTotal = (sizes) => {
  let total = 0;
  ['youth', 'adult', 'coach'].forEach(category => {
    if (sizes[category]) {
      Object.values(sizes[category]).forEach(qty => {
        total += parseInt(qty) || 0;
      });
    }
  });
  return total;
};

// Helper to get total from flat sizes
const getFlatTotal = (sizes) => {
  return Object.values(sizes || {}).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
};

// Helper to calculate product totals - handles both nested and flat sizes
const getProductTotal = (products, productKey) => {
  if (!products || !products[productKey]) return 0;
  const product = products[productKey];

  // Per-team products
  if (product.teamSizes) {
    let total = 0;
    Object.values(product.teamSizes).forEach(sizes => {
      if (isNestedSizes(sizes)) {
        total += getNestedTotal(sizes);
      } else {
        total += getFlatTotal(sizes);
      }
    });
    return total;
  }

  // Bulk products (legacy)
  if (product.sizes) {
    return getFlatTotal(product.sizes);
  }

  return 0;
};

// Helper to get team total for a specific product
const getTeamTotal = (teamSizes) => {
  if (!teamSizes) return 0;
  if (isNestedSizes(teamSizes)) {
    return getNestedTotal(teamSizes);
  }
  return getFlatTotal(teamSizes);
};

const SubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [staffNotes, setStaffNotes] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');

  const fetchSubmission = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/intake-admin/${id}`);
      setSubmission(response.data.submission);
      setNewStatus(response.data.submission.status);
      setStaffNotes(response.data.submission.staff_notes || '');
    } catch (err) {
      setError('Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const handleUpdateStatus = async () => {
    if (newStatus === submission.status && !staffNotes) return;

    setUpdating(true);
    try {
      await api.put(`/intake-admin/${id}/status`, {
        status: newStatus,
        staffNotes: staffNotes !== submission.staff_notes ? staffNotes : undefined,
      });
      await fetchSubmission();
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    setUpdating(true);
    try {
      await api.put(`/intake-admin/${id}/notes`, {
        staffNotes: noteText,
      });
      setNoteText('');
      setShowAddNote(false);
      await fetchSubmission();
    } catch (err) {
      setError('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  const handleConvert = async () => {
    if (!window.confirm('Push this submission to Uniform Orders as a draft?')) return;

    setUpdating(true);
    try {
      const response = await api.post(`/intake-admin/${id}/convert`);

      if (response.data.order) {
        // Order created successfully - redirect to dashboard
        alert(`Order ${response.data.order.orderNumber} created successfully!`);
        navigate(response.data.redirectUrl || '/league-uniform-management');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to push submission');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete submission ${submission.submission_number}? This action cannot be undone.`)) return;

    setUpdating(true);
    try {
      await api.delete(`/intake-admin/${id}`);
      alert('Submission deleted successfully');
      navigate('/intake-queue');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete submission');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      submitted: 'status-badge status-submitted',
      reviewing: 'status-badge status-reviewing',
      approved: 'status-badge status-approved',
      converted: 'status-badge status-converted',
      rejected: 'status-badge status-rejected',
    };
    return <span className={statusClasses[status] || 'status-badge'}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="submission-detail">
        <div className="loading">Loading submission...</div>
      </div>
    );
  }

  if (error && !submission) {
    return (
      <div className="submission-detail">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/intake-queue')} className="btn-secondary">
          Back to Queue
        </button>
      </div>
    );
  }

  const orderDetails = submission.order_details || {};

  return (
    <div className="submission-detail">
      <div className="detail-header">
        <div className="header-left">
          <button onClick={() => navigate('/intake-queue')} className="back-btn">
            &larr; Back to Queue
          </button>
          <h1>{submission.submission_number}</h1>
          {getStatusBadge(submission.status)}
        </div>
        <div className="header-actions">
          {submission.status !== 'converted' && (
            <>
              <button onClick={handleDelete} className="btn-danger" disabled={updating}>
                Delete
              </button>
              <button onClick={handleConvert} className="btn-primary" disabled={updating}>
                {updating ? 'Creating Order...' : 'Push to Uniform Orders'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="detail-content">
        <div className="main-content">
          <div className="section">
            <h2>Contact Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>League Name</label>
                <p>{submission.league_name}</p>
              </div>
              <div className="info-item">
                <label>Contact Name</label>
                <p>{submission.contact_name}</p>
              </div>
              <div className="info-item">
                <label>Email</label>
                <p><a href={`mailto:${submission.contact_email}`}>{submission.contact_email}</a></p>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <p>{submission.contact_phone || '-'}</p>
              </div>
            </div>
          </div>

          <div className="section">
            <h2>Order Details</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Season</label>
                <p>{submission.season || '-'}</p>
              </div>
              <div className="info-item">
                <label>Requested Delivery</label>
                <p>{formatDate(submission.requested_date)}</p>
              </div>
              <div className="info-item">
                <label>Total Teams</label>
                <p>{submission.total_teams}</p>
              </div>
              <div className="info-item">
                <label>Total Items (est.)</label>
                <p>{submission.total_items}</p>
              </div>
            </div>
            {submission.notes && (
              <div className="notes-box">
                <label>Customer Notes</label>
                <p>{submission.notes}</p>
              </div>
            )}
          </div>

          {orderDetails.divisions && orderDetails.divisions.length > 0 && (
            <div className="section">
              <h2>Divisions & Teams</h2>
              {orderDetails.divisions.map((division, i) => (
                <div key={i} className="division-card">
                  <h3>{division.name || `Division ${i + 1}`}</h3>
                  <div className="teams-grid">
                    {division.teams.map((team, j) => (
                      <div key={j} className="team-badge">
                        {team.name || `Team ${j + 1}`}
                        {team.color && <span className="team-color">({team.color})</span>}
                        {team.sponsorName && <span className="team-sponsor"> - Sponsor: {team.sponsorName}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {orderDetails.products && (
            <div className="section">
              <h2>Products Requested</h2>
              <div className="products-detail">
                {/* Jerseys */}
                {getProductTotal(orderDetails.products, 'jerseys') > 0 && (
                  <div className="product-detail-card">
                    <div className="product-detail-header">
                      <h4>Jerseys</h4>
                      <span className="product-detail-type">
                        {TYPE_LABELS[orderDetails.products.jerseys.type] || orderDetails.products.jerseys.type}
                      </span>
                      {orderDetails.products.jerseys.sku && (
                        <span className="product-detail-sku">SKU: {orderDetails.products.jerseys.sku}</span>
                      )}
                      <span className="product-detail-qty">
                        {getProductTotal(orderDetails.products, 'jerseys')} items
                      </span>
                    </div>
                    {orderDetails.products.jerseys.logo && (
                      <div className="product-logo">
                        <img src={orderDetails.products.jerseys.logo.data} alt="Jersey logo" />
                        <span>Logo: {orderDetails.products.jerseys.logo.name}</span>
                      </div>
                    )}
                    <div className="team-sizes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Division</th>
                            <th>Team</th>
                            <th>Sizes</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllTeams(orderDetails.divisions).map(team => {
                            const sizes = orderDetails.products.jerseys.teamSizes?.[team.key];
                            const total = getTeamTotal(sizes);
                            if (total === 0) return null;
                            return (
                              <tr key={team.key}>
                                <td>{team.divisionName}</td>
                                <td><strong>{team.teamName}</strong></td>
                                <td className="sizes-cell">{formatSizes(sizes)}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orderDetails.products.jerseys.notes && (
                      <div className="product-detail-notes">
                        <strong>Notes:</strong> {orderDetails.products.jerseys.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Pants */}
                {getProductTotal(orderDetails.products, 'pants') > 0 && (
                  <div className="product-detail-card">
                    <div className="product-detail-header">
                      <h4>Pants</h4>
                      <span className="product-detail-type">
                        {TYPE_LABELS[orderDetails.products.pants.type] || orderDetails.products.pants.type}
                      </span>
                      {orderDetails.products.pants.sku && (
                        <span className="product-detail-sku">SKU: {orderDetails.products.pants.sku}</span>
                      )}
                      <span className="product-detail-qty">
                        {getProductTotal(orderDetails.products, 'pants')} items
                      </span>
                    </div>
                    <div className="team-sizes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Division</th>
                            <th>Team</th>
                            <th>Sizes</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllTeams(orderDetails.divisions).map(team => {
                            const sizes = orderDetails.products.pants.teamSizes?.[team.key];
                            const total = getTeamTotal(sizes);
                            if (total === 0) return null;
                            return (
                              <tr key={team.key}>
                                <td>{team.divisionName}</td>
                                <td><strong>{team.teamName}</strong></td>
                                <td className="sizes-cell">{formatSizes(sizes)}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orderDetails.products.pants.notes && (
                      <div className="product-detail-notes">
                        <strong>Notes:</strong> {orderDetails.products.pants.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Belts */}
                {getProductTotal(orderDetails.products, 'belts') > 0 && (
                  <div className="product-detail-card">
                    <div className="product-detail-header">
                      <h4>Belts</h4>
                      <span className="product-detail-qty">
                        {getProductTotal(orderDetails.products, 'belts')} items
                      </span>
                    </div>
                    <div className="team-sizes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Division</th>
                            <th>Team</th>
                            <th>Sizes</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllTeams(orderDetails.divisions).map(team => {
                            const sizes = orderDetails.products.belts.teamSizes?.[team.key];
                            const total = getTeamTotal(sizes);
                            if (total === 0) return null;
                            return (
                              <tr key={team.key}>
                                <td>{team.divisionName}</td>
                                <td><strong>{team.teamName}</strong></td>
                                <td className="sizes-cell">{formatSizes(sizes)}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orderDetails.products.belts.notes && (
                      <div className="product-detail-notes">
                        <strong>Notes:</strong> {orderDetails.products.belts.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Socks */}
                {getProductTotal(orderDetails.products, 'socks') > 0 && (
                  <div className="product-detail-card">
                    <div className="product-detail-header">
                      <h4>Socks</h4>
                      <span className="product-detail-type">
                        {TYPE_LABELS[orderDetails.products.socks.type] || orderDetails.products.socks.type}
                      </span>
                      <span className="product-detail-qty">
                        {getProductTotal(orderDetails.products, 'socks')} items
                      </span>
                    </div>
                    <div className="team-sizes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Division</th>
                            <th>Team</th>
                            <th>Sizes</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllTeams(orderDetails.divisions).map(team => {
                            const sizes = orderDetails.products.socks.teamSizes?.[team.key];
                            const total = getTeamTotal(sizes);
                            if (total === 0) return null;
                            return (
                              <tr key={team.key}>
                                <td>{team.divisionName}</td>
                                <td><strong>{team.teamName}</strong></td>
                                <td className="sizes-cell">{formatSockSizes(sizes)}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orderDetails.products.socks.notes && (
                      <div className="product-detail-notes">
                        <strong>Notes:</strong> {orderDetails.products.socks.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Caps */}
                {getProductTotal(orderDetails.products, 'caps') > 0 && (
                  <div className="product-detail-card">
                    <div className="product-detail-header">
                      <h4>Caps</h4>
                      <span className="product-detail-type">
                        {TYPE_LABELS[orderDetails.products.caps.type] || orderDetails.products.caps.type}
                      </span>
                      {orderDetails.products.caps.sku && (
                        <span className="product-detail-sku">SKU: {orderDetails.products.caps.sku}</span>
                      )}
                      <span className="product-detail-qty">
                        {getProductTotal(orderDetails.products, 'caps')} items
                      </span>
                    </div>
                    {orderDetails.products.caps.logo && (
                      <div className="product-logo">
                        <img src={orderDetails.products.caps.logo.data} alt="Cap logo" />
                        <span>Logo: {orderDetails.products.caps.logo.name}</span>
                      </div>
                    )}
                    <div className="team-sizes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Division</th>
                            <th>Team</th>
                            <th>Sizes</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllTeams(orderDetails.divisions).map(team => {
                            const sizes = orderDetails.products.caps.teamSizes?.[team.key];
                            const total = getTeamTotal(sizes);
                            if (total === 0) return null;
                            return (
                              <tr key={team.key}>
                                <td>{team.divisionName}</td>
                                <td><strong>{team.teamName}</strong></td>
                                <td className="sizes-cell">{formatSizes(sizes)}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orderDetails.products.caps.notes && (
                      <div className="product-detail-notes">
                        <strong>Notes:</strong> {orderDetails.products.caps.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="sidebar">
          <div className="section">
            <h2>Status</h2>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="status-select"
              disabled={submission.status === 'converted'}
            >
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
            {newStatus !== submission.status && (
              <button
                onClick={handleUpdateStatus}
                className="btn-primary"
                disabled={updating}
                style={{ marginTop: '12px', width: '100%' }}
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            )}
          </div>

          <div className="section">
            <div className="section-header">
              <h2>Staff Notes</h2>
              <button
                onClick={() => setShowAddNote(!showAddNote)}
                className="btn-small"
              >
                + Add Note
              </button>
            </div>
            {showAddNote && (
              <div className="add-note-form">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note..."
                  rows={3}
                />
                <div className="note-actions">
                  <button onClick={() => setShowAddNote(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button onClick={handleAddNote} className="btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            )}
            {submission.staff_notes ? (
              <div className="staff-notes-content">
                {submission.staff_notes.split('\n\n').map((note, i) => (
                  <div key={i} className="note-entry">{note}</div>
                ))}
              </div>
            ) : (
              <p className="no-notes">No staff notes yet</p>
            )}
          </div>

          <div className="section">
            <h2>Timeline</h2>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <strong>Submitted</strong>
                  <p>{formatDate(submission.created_at)}</p>
                </div>
              </div>
              {submission.reviewed_at && (
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <strong>Reviewed</strong>
                    <p>{formatDate(submission.reviewed_at)}</p>
                    {submission.reviewed_by_name && (
                      <span className="reviewer">by {submission.reviewed_by_name}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetail;
